import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { HorizontalDivider } from 'components/core/divider';

import { Accordion, AccordionSummary, AccordionActions } from 'shared/components';
import ChevronIcon from 'shared/assets/accordion-chevron';
import { Subheading } from '../../typography';

type DetailAccordionProps = {
  id: string;
  title: React.ReactNode;
  children: React.ReactNode;
  isMobile: boolean;
  isFirstSection: boolean;
  isLastSection: boolean;
  hideDivider?: boolean;
  defaultExpanded?: boolean;
};

const DetailAccordion = forwardRef((props: DetailAccordionProps, ref) => {
  const {
    id,
    title,
    children,
    isMobile,
    isLastSection,
    isFirstSection,
    hideDivider = false,
    defaultExpanded = true,
  } = props;

  return (
    <>
      {!hideDivider && <HorizontalDivider mt={isFirstSection ? 24 : 32} mb={24} />}
      <AccordionHolder id={id} ref={ref}>
        <StyledAccordion defaultExpanded={defaultExpanded}>
          <StyledAccordionSummary expandIcon={<StyledChevronIcon />} classes={{ expanded: `expanded` }}>
            <HeaderText>
              <Subheading isMobile={isMobile}>{title}</Subheading>
            </HeaderText>
          </StyledAccordionSummary>
          <StyledAccordionActions>
            <ExpansionContent isLastSection={isLastSection}>{children}</ExpansionContent>
          </StyledAccordionActions>
        </StyledAccordion>
      </AccordionHolder>
    </>
  );
});

export default DetailAccordion;

const StyledAccordion = styled(Accordion)`
  margin: 0 !important;
  min-height: 0 !important;

  &.MuiAccordion-root {
    min-width: 100%;
    background-color: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
  }
  & .MuiAccordionSummary-root {
    height: 32px;
  }
  & .MuiAccordionSummary-root,
  & .MuiAccordionSummary-root.Mui-expanded {
    padding: 0;
    margin: 0 0 20px 0;
    min-height: 0;
  }
`;

const AccordionHolder = styled.div`
  & .expanded > .expanded:first-child {
    margin: 12px 0px !important;
  }
  & .MuiAccordionSummary-expandIcon.Mui-expanded {
    transform: rotate(180deg);
  }
  & .MuiAccordionSummary-expandIcon {
    transform: rotate(0deg);
  }
`;

const StyledChevronIcon = styled(ChevronIcon)`
  height: 8.5px;
  width: 14px;
  margin-left: 7px;
  fill: var(--dcm-product-accordion-icon-color, ${({ theme }) => theme.colors.grey[45]});
`;

const ExpansionContent = styled.div<{ isLastSection?: boolean }>`
  width: 100%;
  padding: 0 0 4px;
  margin-bottom: ${({ isLastSection }) => (isLastSection ? '75px' : '32px')};
  position: relative;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    position: static;
  }
`;

const StyledAccordionActions = styled(AccordionActions)`
  padding: 0 !important;
`;

const HeaderText = styled.div`
  padding-top: 2px;
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  margin: 0 !important;
  height: 24px !important;

  & .MuiAccordionSummary-root {
    display: flex;
  }
  & .MuiAccordionSummary-content,
  .MuiAccordionSummary-content.Mui-expanded {
    margin: 0;
    min-height: 0;
  }

  & .MuiIconButton-label svg {
    margin: 0;
  }

  & .MuiAccordionSummary-content span {
    margin: 0;
  }
`;
